import { createTheme } from '@mui/material/styles';
import tokens from './tokens';
import components from './components';
import layout from './layout';

// Create Material-UI theme with privacv design system - light mode only
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.colors.primary.main,
      light: tokens.colors.primary.light,
      dark: tokens.colors.primary.dark,
      contrastText: tokens.colors.primary.contrast,
    },
    secondary: {
      main: tokens.colors.secondary.main,
      light: tokens.colors.secondary.light,
      dark: tokens.colors.secondary.dark,
      contrastText: tokens.colors.secondary.contrast,
    },
    error: {
      main: tokens.colors.error,
    },
    warning: {
      main: tokens.colors.warning,
    },
    success: {
      main: tokens.colors.success,
    },
    grey: tokens.colors.neutral,
    background: {
      default: tokens.colors.background.default,
      paper: tokens.colors.background.paper,
    },
    text: {
      primary: tokens.colors.neutral[900],
      secondary: tokens.colors.text.secondary,
    },
  },
  typography: {
    fontFamily: tokens.typography.fontFamily.primary,
    fontSize: 14,
    h1: {
      fontSize: tokens.typography.fontSizes['3xl'],
      fontWeight: tokens.typography.fontWeights.bold,
      letterSpacing: tokens.typography.letterSpacing.tight,
      lineHeight: tokens.typography.lineHeights.tight,
    },
    h2: {
      fontSize: tokens.typography.fontSizes['2xl'],
      fontWeight: tokens.typography.fontWeights.semibold,
      letterSpacing: tokens.typography.letterSpacing.tight,
      lineHeight: tokens.typography.lineHeights.tight,
    },
    h3: {
      fontSize: tokens.typography.fontSizes.xl,
      fontWeight: tokens.typography.fontWeights.semibold,
      lineHeight: tokens.typography.lineHeights.tight,
    },
    h4: {
      fontSize: tokens.typography.fontSizes.lg,
      fontWeight: tokens.typography.fontWeights.medium,
      lineHeight: tokens.typography.lineHeights.tight,
    },
    h5: {
      fontSize: tokens.typography.fontSizes.base,
      fontWeight: tokens.typography.fontWeights.medium,
    },
    h6: {
      fontSize: tokens.typography.fontSizes.sm,
      fontWeight: tokens.typography.fontWeights.medium,
    },
    body1: {
      fontSize: tokens.typography.fontSizes.base,
      lineHeight: tokens.typography.lineHeights.normal,
    },
    body2: {
      fontSize: tokens.typography.fontSizes.sm,
      lineHeight: tokens.typography.lineHeights.normal,
    },
    button: {
      textTransform: 'none',
      fontWeight: tokens.typography.fontWeights.medium,
    },
    caption: {
      fontSize: tokens.typography.fontSizes.xs,
      color: tokens.colors.text.secondary,
    },
    overline: {
      fontSize: tokens.typography.fontSizes.xs,
      fontWeight: tokens.typography.fontWeights.medium,
      letterSpacing: tokens.typography.letterSpacing.wider,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: parseInt(tokens.radius.base.replace('rem', '')) * 16, // Convert from rem to px
  },
  shadows: [
    'none',
    tokens.shadows.sm,          // 1
    tokens.shadows.base,        // 2
    tokens.shadows.md,          // 3
    tokens.shadows.lg,          // 4
    tokens.shadows.soft,        // 5
    tokens.shadows.soft,        // 6
    tokens.shadows.soft,        // 7
    tokens.shadows.elegant,     // 8
    tokens.shadows.elegant,     // 9
    tokens.shadows.elegant,     // 10
    tokens.shadows.elegant,     // 11
    tokens.shadows.elegant,     // 12
    tokens.shadows.elegant,     // 13
    tokens.shadows.elegant,     // 14
    tokens.shadows.elegant,     // 15
    tokens.shadows.elegant,     // 16
    tokens.shadows.highlight,   // 17
    tokens.shadows.highlight,   // 18
    tokens.shadows.highlight,   // 19
    tokens.shadows.highlight,   // 20
    tokens.shadows.highlight,   // 21
    tokens.shadows.highlight,   // 22
    tokens.shadows.highlight,   // 23
    tokens.shadows.highlight,   // 24
  ],
  transitions: {
    easing: {
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      // Add custom easing functions
      bounce: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  breakpoints: {
    values: layout.breakpoints.values,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: components.button.base,
        contained: components.button.primary,
        outlined: components.button.outlined,
        text: components.button.text,
      },
      variants: [
        {
          props: { variant: 'secondary' },
          style: components.button.secondary,
        },
        {
          props: { variant: 'destructive' },
          style: components.button.destructive,
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: components.card.root,
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: components.card.header,
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: components.card.content,
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          ...components.input.root,
          ...(components.textField?.root || {}),
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: components.input.label,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: components.iconButton.root,
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: components.appBar.root,
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: components.appBar.toolbar,
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: components.dialog.paper,
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: components.dialog.title,
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: components.dialog.content,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: components.dialog.actions,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: components.menu.paper,
        list: components.menu.list,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: components.menu.item,
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: components.tooltip.tooltip,
        arrow: components.tooltip.arrow,
      },
    },
    // Add more component overrides as needed
  },
});

// Export everything as named exports
export { tokens, components, layout };
export default theme; 